import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider
} from '@mui/material';
import {
  Tag as TagIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Notes as NotesIcon
} from '@mui/icons-material';
import { Job } from '../../services/jobService';

interface JobDetailsCardProps {
  job: Job;
  theme: any;
}

const JobDetailsCard: React.FC<JobDetailsCardProps> = ({ job, theme }) => {
  // Import the jobService here since it's needed for formatting
  const jobService = require('../../services/jobService').jobService;

  return (
    <Card sx={{ backgroundColor: theme.accentColor }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Job Details
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          <TagIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Job Number
            </Typography>
            <Typography variant="body1">
              {job.jobNumber}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          <WorkIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Type
            </Typography>
            <Typography variant="body1">
              {jobService.getJobTypeDisplayName(job.type)}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          <WorkIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box>
              <Chip
                label={job.statusName}
                size="small"
                style={{ backgroundColor: job.statusColor, color: 'white' }}
              />
            </Box>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          <WorkIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Priority
            </Typography>
            <Box>
              <Chip
                label={jobService.getPriorityDisplayName(job.priority)}
                size="small"
                style={{ backgroundColor: jobService.getPriorityColor(job.priority), color: 'white' }}
              />
            </Box>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          <PersonIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Assigned To
            </Typography>
            <Typography variant="body1">
              {job.assignedSalesPersonName}
            </Typography>
          </Box>
        </Box>

        {job.jobAddress && (
          <Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
            <LocationIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary', mt: 0.5 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Job Address
              </Typography>
              <Typography variant="body1">
                {job.jobAddress}
              </Typography>
            </Box>
          </Box>
        )}

        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          <MoneyIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Estimated Value
            </Typography>
            <Typography variant="body1">
              {jobService.formatCurrency(job.estimatedValue)}
            </Typography>
          </Box>
        </Box>

        {job.actualValue && (
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <MoneyIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Actual Value
              </Typography>
              <Typography variant="body1">
                {jobService.formatCurrency(job.actualValue)}
              </Typography>
            </Box>
          </Box>
        )}

        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          <CalendarIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Created Date
            </Typography>
            <Typography variant="body1">
              {new Date(job.createdDate).toLocaleDateString('en-GB')}
            </Typography>
          </Box>
        </Box>

        {job.expectedCompletionDate && (
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <CalendarIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Expected Completion
              </Typography>
              <Typography variant="body1">
                {new Date(job.expectedCompletionDate).toLocaleDateString('en-GB')}
              </Typography>
            </Box>
          </Box>
        )}

        {job.notes && (
          <Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
            <NotesIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary', mt: 0.5 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body1">
                {job.notes}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default JobDetailsCard;
